import type { Feature, Position } from "geojson";
import { cloneDeep } from "lodash-es";

import { hasCoordinates } from "./geometryUtils";

/**
 * Add a coordinate at a specific nested index position in a feature's geometry.
 * Supports LineString, MultiLineString, Polygon, and MultiPolygon geometry types.
 *
 * @param feature - The GeoJSON feature to modify.
 * @param nestedIdx - A string representing the nested index path (e.g. "0.1.2") from metaCoordinateIdx.
 * @param newCoordinate - The new coordinate to insert [lng, lat] or [lng, lat, alt].
 * @returns The feature with the coordinate added at the specified position.
 */
export default function addNestedCoordinate(
  feature: Feature,
  nestedIdx: string,
  newCoordinate: Position,
): Feature {
  const geometry = feature.geometry;

  if (!geometry) {
    throw new Error("Feature geometry is null");
  }

  if (geometry.type === "GeometryCollection") {
    throw new Error("Cannot add coordinate to GeometryCollection");
  }

  if (geometry.type === "Point" || geometry.type === "MultiPoint") {
    throw new Error(`Cannot add coordinate to ${geometry.type} geometry`);
  }

  if (!hasCoordinates(geometry)) {
    throw new Error("Geometry does not have coordinates");
  }

  const indices = nestedIdx.split(".").map(Number);

  switch (geometry.type) {
    case "LineString": {
      if (indices.length !== 1) {
        throw new Error("LineString expects single index, got: " + nestedIdx);
      }

      const coordIndex = indices[0];
      const coords = cloneDeep(geometry.coordinates); // Position[]
      coords.splice(coordIndex + 1, 0, newCoordinate);

      return {
        ...feature,
        geometry: {
          ...geometry,
          coordinates: coords,
        },
      };
    }

    case "MultiLineString": {
      if (indices.length !== 2) {
        throw new Error(
          "MultiLineString expects two indices, got: " + nestedIdx,
        );
      }

      const [lineIndex, lineCoordIndex] = indices;
      const coords = cloneDeep(geometry.coordinates); // Position[][]
      if (lineIndex >= coords.length) {
        throw new Error(`Line index ${lineIndex} out of bounds`);
      }

      coords[lineIndex].splice(lineCoordIndex + 1, 0, newCoordinate);

      return {
        ...feature,
        geometry: {
          ...geometry,
          coordinates: coords,
        },
      };
    }

    case "Polygon": {
      if (indices.length !== 2) {
        throw new Error("Polygon expects two indices, got: " + nestedIdx);
      }

      const [ringIndex, ringCoordIndex] = indices;
      const coords = cloneDeep(geometry.coordinates); // Position[][]
      if (ringIndex >= coords.length) {
        throw new Error(`Ring index ${ringIndex} out of bounds`);
      }

      coords[ringIndex].splice(ringCoordIndex + 1, 0, newCoordinate);

      return {
        ...feature,
        geometry: {
          ...geometry,
          coordinates: coords,
        },
      };
    }

    case "MultiPolygon": {
      if (indices.length !== 3) {
        throw new Error(
          "MultiPolygon expects three indices, got: " + nestedIdx,
        );
      }

      const [polyIndex, ringIndex, coordIndex] = indices;
      const coords = cloneDeep(geometry.coordinates); // Position[][][]
      if (polyIndex >= coords.length) {
        throw new Error(`Polygon index ${polyIndex} out of bounds`);
      }
      if (ringIndex >= coords[polyIndex].length) {
        throw new Error(`Ring index ${ringIndex} out of bounds`);
      }

      coords[polyIndex][ringIndex].splice(coordIndex + 1, 0, newCoordinate);

      return {
        ...feature,
        geometry: {
          ...geometry,
          coordinates: coords,
        },
      };
    }

    default:
      // This should be unreachable because we excluded unsupported types above
      throw new Error(`Unsupported geometry type: ${(geometry as any).type}`);
  }
}
